import { z } from 'zod';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import type { LineComparisonRow } from './rfq.js';

/**
 * Satır satır seçim (04.11 notları D4; ADR 0166 §2): ofis her RFQ kalemi için ayrı tedarikçi
 * seçer; tedarikçi başına bir doğrudan ödül ve bir PO çıkar. Seçilebilen hücre fiyatlı ya da ikame
 * yanıtıdır (fiyatı olan), RFQ para biriminde ve bugün onaylı tedarikçinin. Bir satırı reddeden ya
 * da fiyatsız bırakan teklifin fiyatlı satırları seçilebilir; seçilmeyen satır açık kalır. Satırın
 * en iyi fiyatı varken başka seçim gerekçe ister (SERTICA ve BASS'ta "reason for selection";
 * denetimde iz bırakır [NR496 4.3.2a]). Hata iletileri koddur; arayüz çevirir.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});

export const LINE_SELECTION_KINDS = ['priced', 'substitute'] as const;
export type LineSelectionKind = (typeof LINE_SELECTION_KINDS)[number];

export const lineSelectionSchema = z
  .strictObject({
    lines: z
      .array(
        z.strictObject({
          rfqLineId: z.uuid(typed('INVALID_ID')),
          quotationId: z.uuid(typed('INVALID_ID')),
          kind: z.enum(LINE_SELECTION_KINDS, typed('INVALID_KIND')),
        }),
        typed('INVALID_LINES'),
      )
      .min(1, 'NO_LINES')
      .max(200, 'TOO_MANY_LINES'),
    /** En ucuz olmayan satır seçiminin gerekçesi (seçimin bütünü için tek metin). */
    reason: z
      .string(typed('INVALID_TEXT'))
      .transform((value) => value.normalize('NFC').trim())
      .pipe(z.string().max(1000, 'TOO_LONG'))
      .transform((value) => (value === '' ? null : value))
      .nullish()
      .transform((value) => value ?? null),
  })
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    for (const [index, line] of value.lines.entries()) {
      if (seen.has(line.rfqLineId)) {
        ctx.addIssue({ code: 'custom', message: 'DUPLICATE_LINE', path: ['lines', index] });
      }
      seen.add(line.rfqLineId);
    }
  });
export type LineSelection = z.output<typeof lineSelectionSchema>;

export function parseLineSelection(input: unknown): ParseResult<LineSelection> {
  const result = lineSelectionSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export interface SelectedLine {
  rfqLineId: string;
  quotationId: string;
  kind: LineSelectionKind;
  /** Fiyatlıda RFQ birimine, ikamede ikame birimine birim fiyat (tekliften). */
  unitPrice: number;
  /** Satırın en iyi fiyatı mı (seçim anında). */
  best: boolean;
}

/**
 * Seçimi satır karşılaştırmasına (`compareLines`) karşı denetler ve seçilen satırları fiyatıyla
 * döndürür. Bilinmeyen satır `UNKNOWN_RFQ_LINE`; seçilemeyen hücre `LINE_NOT_SELECTABLE`; başka
 * para birimi ve onaysız tedarikçi kendi kodlarıyla; en iyi olmayan seçim gerekçesizse
 * `SELECTION_REASON_REQUIRED`.
 */
export function resolveLineSelection(
  comparison: { lines: readonly LineComparisonRow[] },
  selection: LineSelection,
): SelectedLine[] {
  const rows = new Map(comparison.lines.map((row) => [row.rfqLineId, row]));
  const chosen: SelectedLine[] = [];
  for (const pick of selection.lines) {
    const row = rows.get(pick.rfqLineId);
    if (!row) throw new DomainError('UNKNOWN_RFQ_LINE', pick.rfqLineId);
    const cell = row.cells.find((c) => c.quotationId === pick.quotationId);
    if (!cell) throw new DomainError('LINE_NOT_SELECTABLE', pick.rfqLineId);
    if (!cell.eligible) throw new DomainError(cell.reason ?? 'LINE_NOT_SELECTABLE', pick.rfqLineId);
    const fits =
      pick.kind === 'priced'
        ? cell.response === 'priced' && !cell.substitute
        : cell.response === 'substitute' && cell.substitute && cell.total !== null;
    if (!fits || cell.unitPrice === null) {
      throw new DomainError('LINE_NOT_SELECTABLE', pick.rfqLineId);
    }
    chosen.push({
      rfqLineId: pick.rfqLineId,
      quotationId: pick.quotationId,
      kind: pick.kind,
      unitPrice: cell.unitPrice,
      // Satırın fiyatlı en iyisi yoksa (yalnız ikame) seçim en iyidir: gerekçe istenmez
      best: row.bestUnitPrice === null || (pick.kind === 'priced' && cell.best),
    });
  }
  if (chosen.some((line) => !line.best) && !selection.reason) {
    throw new DomainError('SELECTION_REASON_REQUIRED');
  }
  return chosen;
}
